import React from 'react'
import { Box, Flex, Heading, Image, Skeleton, Text } from '@chakra-ui/react'
import Link from 'next/link'
import { ResultOf } from '@graphql-typed-document-node/core'
import { gql } from '@urql/core'
import { AuthorInfo, Blog, blogAuthorName } from 'entities/blog'
import { blog as mockBlog } from 'entities/blog.mock'
import { routes } from 'routes'
import { toShortAndLongDates } from 'lib/date'
import { ImgixParams } from 'lib/imgix'
import { Avatar } from 'components/images/Avatar'

export const defaultImgixParams = {
    fit: 'crop',
    crop: 'center',
} as ImgixParams

export const FRAGMENT_BLOG_CARD = gql<
    Pick<Blog, 'id' | 'title' | 'feature' | 'creationDate' | 'updated' | 'author'> & {
        authorInfo: Pick<AuthorInfo, 'id' | 'name' | 'imageUrl'>
    }
>`
    fragment BlogCard on Blog {
        id
        title
        feature
        creationDate
        updated
        author
        authorInfo {
            id
            name
            imageUrl
        }
    }
`

export type BlogCardData = ResultOf<typeof FRAGMENT_BLOG_CARD>

export const BlogCard = ({ blog }: { blog?: BlogCardData }) => {
    const isLoaded = Boolean(blog)

    const { id, title, feature, creationDate, updated, authorInfo } = blog ?? mockBlog
    const href = id ? routes.article({ id }) : '#'

    const publishedDate = updated ? updated : creationDate
    const [shortDisplayDate] = toShortAndLongDates(publishedDate)

    // TODO: CU-2u4rzyw use Image component from 'next/image'
    // this loader was introduced because without it
    // getServerSideProps fn on the blog page was called twice
    // const loader = ({ src }: { src: string }) => {
    //     return src ? toImgixUrl(src, defaultImgixParams) : '#'
    // }

    const imgBorderRadius = '12px 12px 0 0'

    return (
        <Link href={href} passHref>
            <Flex
                as="a"
                direction="column"
                gridGap="16px"
                borderWidth="1px"
                borderRadius="12px"
                bgColor="white"
            >
                <Skeleton borderRadius={imgBorderRadius} isLoaded={isLoaded}>
                    <Box position="relative" __css={{ aspectRatio: '1.7' }}>
                        <Image
                            h="100%"
                            w="100%"
                            src={feature}
                            // loader={loader}
                            // layout="fill"
                            alt={title}
                            objectFit="cover"
                            objectPosition="50% 50%"
                            borderRadius={imgBorderRadius}
                        />
                    </Box>
                </Skeleton>
                <Flex
                    direction="column"
                    gridGap="12px"
                    p={{ base: '0 16px 16px 16px', md: '0 24px 24px 24px' }}
                >
                    <Skeleton isLoaded={isLoaded}>
                        <Heading as="h3" size="sm" noOfLines={2}>
                            {title}
                        </Heading>
                    </Skeleton>
                    <Skeleton isLoaded={isLoaded}>
                        <Flex gridGap="8px" align="center">
                            {authorInfo && (
                                <Avatar
                                    variant="user"
                                    src={authorInfo.imageUrl}
                                    boxSize="24px"
                                />
                            )}
                            <Text variant="functionalNormal" noOfLines={1}>
                                {blogAuthorName(blog ?? mockBlog)}
                            </Text>
                            <Text fontSize="18px" lineHeight="16px" w="4px" ml="-1px">
                                {'•'}
                            </Text>
                            <Text
                                variant="functionalNormal"
                                flexShrink={0}
                                whiteSpace="nowrap"
                            >
                                {shortDisplayDate}
                            </Text>
                        </Flex>
                    </Skeleton>
                </Flex>
            </Flex>
        </Link>
    )
}
